import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as artifactService from '../../services/artifactService';
import { useTheme } from '../../contexts/ThemeContext';

interface DiffViewerProps {
  artifactId: string;
  versionA: string;
  versionB: string;
}

type DiffFormat = 'unified' | 'side-by-side' | 'word';

export const DiffViewer: React.FC<DiffViewerProps> = ({
  artifactId,
  versionA,
  versionB
}) => {
  const { darkMode } = useTheme();
  const [diffFormat, setDiffFormat] = useState<DiffFormat>('unified');
  const [contextLines, setContextLines] = useState(3);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  
  const { data: diffData, isLoading, error } = useQuery(
    ['artifactDiff', artifactId, versionA, versionB, diffFormat, contextLines, ignoreWhitespace],
    () => artifactService.compareArtifactVersions(
      artifactId, 
      versionA, 
      versionB, 
      diffFormat, 
      contextLines, 
      ignoreWhitespace
    )
  );
  
  const renderUnifiedDiff = () => {
    if (!diffData || !diffData.diff) return null;

    return (
      <div className={`unified-diff font-mono text-sm p-4 rounded border overflow-auto ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        {diffData.diff.map((line: any, index: number) => {
          let className = '';
          if (line.type === 'addition') {
            className = darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
          } else if (line.type === 'deletion') {
            className = darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
          } else if (line.type === 'hunk_header') {
            className = darkMode ? 'bg-gray-700 text-gray-300 font-bold' : 'bg-gray-200 text-gray-700 font-bold';
          } else {
            className = darkMode ? 'text-gray-300' : '';
          }

          return (
            <div key={index} className={`${className} px-2 py-0.5`}>
              {line.type === 'addition' && <span className={`mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>+</span>}
              {line.type === 'deletion' && <span className={`mr-2 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>-</span>}
              {line.type === 'context' && <span className="text-gray-400 mr-2"> </span>}
              {line.content}
            </div>
          );
        })}
      </div>
    );
  };
  
  const renderSideBySideDiff = () => {
    if (!diffData || !diffData.diff) return null;

    return (
      <div className="side-by-side-diff overflow-auto">
        <table className="w-full text-sm font-mono">
          <tbody>
            {diffData.diff.map((line: any, index: number) => {
              let leftClassName = 'px-2 py-0.5';
              let rightClassName = 'px-2 py-0.5';

              if (line.tag === 'delete') {
                leftClassName += darkMode ? ' bg-red-900/40 text-red-300' : ' bg-red-100 text-red-800';
              } else if (line.tag === 'insert') {
                rightClassName += darkMode ? ' bg-green-900/40 text-green-300' : ' bg-green-100 text-green-800';
              } else if (line.tag === 'replace') {
                leftClassName += darkMode ? ' bg-yellow-900/40 text-yellow-300' : ' bg-yellow-100 text-yellow-800';
                rightClassName += darkMode ? ' bg-yellow-900/40 text-yellow-300' : ' bg-yellow-100 text-yellow-800';
              } else {
                leftClassName += darkMode ? ' text-gray-300' : '';
                rightClassName += darkMode ? ' text-gray-300' : '';
              }

              return (
                <tr key={index}>
                  <td className="text-right text-gray-500 w-10 pr-2 select-none">{line.left_line_number}</td>
                  <td className={leftClassName}>{line.left_content}</td>
                  <td className="text-right text-gray-500 w-10 pr-2 select-none">{line.right_line_number}</td>
                  <td className={rightClassName}>{line.right_content}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  
  const renderWordDiff = () => {
    if (!diffData || !diffData.diff) return null;

    return (
      <div className={`word-diff font-mono text-sm p-4 rounded border ${
        darkMode ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200'
      }`}>
        {diffData.diff.map((segment: any, index: number) => {
          let className = '';
          if (segment.type === 'addition') {
            className = darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
          } else if (segment.type === 'deletion') {
            className = darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
          }
          return (
            <span key={index} className={className}>
              {segment.content}
            </span>
          );
        })}
      </div>
    );
  };
  
  if (isLoading) {
    return <div className={`text-center py-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading diff...</div>;
  }

  if (error) {
    return (
      <div className={`text-center py-4 rounded-lg p-4 ${
        darkMode ? 'bg-red-900/20 border border-red-700 text-red-400' : 'text-red-600'
      }`}>
        Error loading diff
      </div>
    );
  }

  return (
    <div className="diff-viewer">
      <div className={`mb-4 p-4 rounded-lg border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white shadow border-transparent'
      }`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Comparing Versions</h3>
            <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {diffData?.version_a.number} ({diffData?.version_a.agent_name}) → {diffData?.version_b.number} ({diffData?.version_b.agent_name})
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Format
              </label>
              <select
                value={diffFormat}
                onChange={(e) => setDiffFormat(e.target.value as DiffFormat)}
                className={`border rounded px-2 py-1 text-sm ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="unified">Unified</option>
                <option value="side-by-side">Side by Side</option>
                <option value="word">Word</option>
              </select>
            </div>

            {diffFormat === 'unified' && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Context Lines
                </label>
                <select
                  value={contextLines}
                  onChange={(e) => setContextLines(Number(e.target.value))}
                  className={`border rounded px-2 py-1 text-sm ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value={1}>1 Line</option>
                  <option value={3}>3 Lines</option>
                  <option value={5}>5 Lines</option>
                  <option value={10}>10 Lines</option>
                </select>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="ignore-whitespace"
                checked={ignoreWhitespace}
                onChange={(e) => setIgnoreWhitespace(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="ignore-whitespace" className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Ignore Whitespace
              </label>
            </div>
          </div>
        </div>

        {diffData && (
          <div className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="font-medium">Changes:</span> {diffData.summary}
          </div>
        )}
      </div>

      <div className={`rounded-lg border overflow-hidden ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white shadow border-transparent'
      }`}>
        {diffFormat === 'unified' && renderUnifiedDiff()}
        {diffFormat === 'side-by-side' && renderSideBySideDiff()}
        {diffFormat === 'word' && renderWordDiff()}
      </div>
    </div>
  );
};
